import { FunnelGroupConfig } from '@/types/analytics/funnels.types';
import { MetadataTypeId } from '@/types/analytics/analytics.types';
import { operators as o, common as c } from '@pendo/aggregations';

type GroupingPipelineConfig = {
    bulkExpand?: c.PipelineData;
    evals: c.PipelineData[];
    selects: { [key: string]: string };
    columns: string[];
    groupingByAccountMetadata: boolean;
};

export default function generateGroupingPipelines (groupings: FunnelGroupConfig[] = []): GroupingPipelineConfig {
    const pipelines: GroupingPipelineConfig = {
        evals: [],
        selects: {},
        columns: [],
        groupingByAccountMetadata: false,
    };

    groupings.forEach((grouping: FunnelGroupConfig, index: number) => {
        if (!grouping.configuration) return;

        const groupingByAccountMetadata = [MetadataTypeId.Account, MetadataTypeId.ParentAccount].includes(
            grouping.type,
        );
        if (!pipelines.groupingByAccountMetadata && groupingByAccountMetadata) {
            pipelines.groupingByAccountMetadata = true;
        }

        const groupByKey = `groupValue${index}`;
        const { kind } = grouping.configuration;

        const isEventOrHistorical = [
            MetadataTypeId.Historical,
            MetadataTypeId.Event,
            MetadataTypeId.Automatic,
            MetadataTypeId.Utm,
        ].includes(grouping.type);

        const isVisitorIdOrAccountId = [MetadataTypeId.Visitor, MetadataTypeId.Account].includes(grouping.type) && grouping.configuration?.field === 'id';
        if (!pipelines.bulkExpand && !isEventOrHistorical && !isVisitorIdOrAccountId) {
            pipelines.bulkExpand = o.bulkExpand(kind);
        }
        pipelines.evals = generateGroupingEvals(grouping, groupByKey);
        pipelines.selects[groupByKey] = groupByKey;
        pipelines.columns.push(groupByKey);
    });

    return pipelines;
}

function generateGroupingEvals (grouping: FunnelGroupConfig, groupByKey: string): c.PipelineData[] {
    const groupByEvals: c.PipelineData[] = [];
    const { kind, group, field } = grouping.configuration;
    const isEventOrHistorical = [
        MetadataTypeId.Historical,
        MetadataTypeId.Event,
        MetadataTypeId.Automatic,
        MetadataTypeId.Utm,
    ].includes(grouping.type);

    let groupByEval = `${kind}.${group}.${field}`;
    if (isEventOrHistorical) {
        groupByEval = 'funnel.epValues[0]';
    }
    let isVisitorIdOrAccountId = false;
    if (grouping.type === MetadataTypeId.Visitor && field === 'id') {
        groupByEval = 'visitorId';
        isVisitorIdOrAccountId = true;
    }
    if (grouping.type === MetadataTypeId.Account && field === 'id') {
        groupByEval = 'accountId';
        isVisitorIdOrAccountId = true;
    }

    groupByEvals.push(o.evaluate(groupByKey, groupByEval));
    if (!isVisitorIdOrAccountId) {
        groupByEvals.push(o.evaluate(groupByKey, `if(isNull(${groupByKey}), "(none)", ${groupByKey})`));
        groupByEvals.push(
            o.evaluate(groupByKey, `if(${groupByKey} == true, "true", if(${groupByKey} == false, "false", ${groupByKey}))`),
        );
        groupByEvals.push(
            o.evaluate(groupByKey, `if(isString(${groupByKey}), trim(trim(${groupByKey}, "\\n"), " "), ${groupByKey})`),
        );
        groupByEvals.push(o.evaluate(groupByKey, `if(${groupByKey} == "", "(empty)", ${groupByKey})`));
    }

    return groupByEvals;
}
